import React, { useEffect, useState, useRef, useCallback } from "react";
import AppHeader from "../components/AppHeader";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Wallet, PiggyBank, FileText, User, Lock, Eye, Download,
  ShieldCheck, ShieldAlert, ShieldX, FileLock2, BadgeCheck, UserCheck, Phone, Mail, MapPin, MessageSquare, PhoneCall, CheckCircle2, Users, XCircle,
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { API_BASE } from "../lib/api";
import FooterCaixaBank from "../components/FooterCaixaBank";

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

// Libellé bancaire du/des titulaire(s) du compte
const formatHolders = (u) => {
  if (!u) return "";
  const clean = (s) => (s || "").trim();
  const abbrev = (c) => (c === "Monsieur" ? "M." : c === "Madame" ? "Mme" : clean(c));
  const fullName = (h) => `${h.civ ? abbrev(h.civ) + " " : ""}${clean(h.first)} ${clean(h.last)}`.replace(/\s+/g, " ").trim();
  const main = { civ: clean(u.civility), first: clean(u.first_name), last: clean(u.last_name) };
  const hasCo = clean(u.co_civility) || clean(u.co_first_name) || clean(u.co_last_name);
  if (!hasCo) return fullName(main);
  const co = { civ: clean(u.co_civility), first: clean(u.co_first_name), last: clean(u.co_last_name) };
  // Ordre bancaire : Monsieur avant Madame
  const rank = (c) => (c === "Monsieur" ? 0 : c === "Madame" ? 1 : 2);
  let a = main, b = co;
  if (rank(b.civ) < rank(a.civ)) { const t = a; a = b; b = t; }
  const sameLast = a.last && b.last && a.last.toLowerCase() === b.last.toLowerCase();
  if (sameLast) {
    const civs = [a.civ, b.civ].filter(Boolean).map(abbrev).join(" et ");
    return `${civs ? civs + " " : ""}${a.last}`.trim();
  }
  return `${fullName(a)} et ${fullName(b)}`.replace(/\s+/g, " ").trim();
};

// Indique si des données de civilité/co-titulaire existent (pour affichage RIB automatique)
const hasHolderCivility = (u) =>
  !!(u && ((u.civility || "").trim() || (u.co_civility || "").trim() || (u.co_first_name || "").trim() || (u.co_last_name || "").trim()));

// Nom affiché du compte : pour une SCI on affiche la raison sociale, sinon le(s) titulaire(s)
const isSciAccount = (u) => u?.account_type === "sci";
const displayName = (u) => {
  if (!u) return "";
  if (isSciAccount(u)) return (u.company_name || "").trim() || "Société (SCI)";
  return formatHolders(u);
};
const formatRepresentative = (r) => {
  const abbrev = (c) => (c === "Monsieur" ? "M." : c === "Madame" ? "Mme" : (c || ""));
  return `${r.civility ? abbrev(r.civility) + " " : ""}${(r.first_name || "").trim()} ${(r.last_name || "").trim()}`.replace(/\s+/g, " ").trim();
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { label: "En attente d'approbation", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: ShieldAlert },
    approved: { label: "Compte approuvé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: BadgeCheck },
    blocked:  { label: "Compte bloqué", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: ShieldX },
  };
  const it = map[status] || map.pending;
  const I = it.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[12px] font-medium ${it.cls}`}>
      <I className="w-3.5 h-3.5" /> {it.label}
    </span>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
      active
        ? "border-[#007EAE] text-[#007EAE] bg-white"
        : "border-transparent text-slate-600 hover:text-[#007EAE] hover:bg-white"
    }`}
  >
    <Icon className="w-4 h-4" /> {children}
  </button>
);

// ---- Mon compte
const AccountTab = ({ user }) => {
  const pending = user?.status !== "approved";
  const [repsOpen, setRepsOpen] = useState(false);
  const reps = Array.isArray(user?.representatives) ? user.representatives : [];
  const isSci = isSciAccount(user);
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
            {isSci ? "SCI" : "Bonjour"} {displayName(user) || user?.first_name || user?.email} 👋
          </h2>
          <p className="text-slate-500 text-sm">Aperçu de votre dossier de financement.</p>
          {isSci && (
            <button type="button" onClick={() => setRepsOpen(true)} data-testid="reps-btn"
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#007EAE] text-[#007EAE] hover:bg-[#007EAE]/5 text-[13px] font-semibold btn-press">
              <Users className="w-4 h-4" /> Représentants légaux{reps.length ? ` (${reps.length})` : ""}
            </button>
          )}
        </div>
        <StatusBadge status={user?.status} />
      </div>

      {repsOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={() => setRepsOpen(false)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center justify-between bg-[#007EAE] text-white">
              <h3 className="font-semibold text-lg">Représentants légaux de la société</h3>
              <button type="button" onClick={() => setRepsOpen(false)} className="text-white/90 hover:text-white"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-2">
              {reps.length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun représentant renseigné.</p>
              ) : reps.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-3 border border-slate-200">
                  <div className="w-9 h-9 bg-[#007EAE]/10 flex items-center justify-center shrink-0">
                    <UserCheck className="w-5 h-5 text-[#007EAE]" />
                  </div>
                  <p className="font-semibold text-slate-800">{formatRepresentative(r)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pending && (
        <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Votre dossier est en cours d'approbation.</p>
            <p>Les montants ci-dessous sont indicatifs. Vous serez notifié dès qu'ils seront officiellement approuvés par votre banque.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-6 bg-white border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm">Mon compte crédit immobilier</p>
              <p className="text-3xl font-bold text-[#003E7E] mt-1">{formatEUR(user?.credit_mobilier)}</p>
            </div>
            <div className="w-12 h-12 bg-[#007EAE]/10 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-[#007EAE]" />
            </div>
          </div>
          {pending && <p className="mt-3 text-[12px] text-amber-700">Montant en attente d'approbation</p>}
        </div>
        <div className="p-6 bg-white border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm">Mon apport</p>
              <p className="text-3xl font-bold text-[#003E7E] mt-1">{formatEUR(user?.apport)}</p>
            </div>
            <div className="w-12 h-12 bg-[#FFC72C]/20 flex items-center justify-center">
              <PiggyBank className="w-6 h-6 text-[#a37908]" />
            </div>
          </div>
          {pending && <p className="mt-3 text-[12px] text-amber-700">Montant en attente d'approbation</p>}
          
          {/* Barre de progression de l'apport */}
          {user?.loan_contribution && user?.loan_received && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                <span>Versement de l'apport personnel</span>
                <span className="font-semibold text-[#007EAE]">
                  {formatEUR(user.loan_received)} / {formatEUR(user.loan_contribution)}
                </span>
              </div>
              <div className="w-full bg-slate-200 h-2.5">
                <div
                  className="bg-[#007EAE] h-2.5 transition-all duration-500"
                  style={{
                    width: `${Math.min(100, (user.loan_received / user.loan_contribution) * 100)}%`
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                <span>
                  {Math.min(100, Math.round((user.loan_received / user.loan_contribution) * 100))}% versé
                </span>
                <span>
                  Restant : {formatEUR(Math.max(0, user.loan_contribution - user.loan_received))}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 text-[#003E7E] font-semibold">
          <ShieldCheck className="w-5 h-5" /> Sécurité de votre espace
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Votre espace est sécurisé par chiffrement bancaire. Pour toute question, contactez votre conseiller CaixaBank.
        </p>
      </div>

      {/* Détails du prêt - Affichage conditionnel */}
      {(user?.loan_amount || user?.loan_duration || user?.loan_rate || user?.loan_monthly) && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-slate-800">
            <FileText className="w-5 h-5 text-[#007EAE]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Georgia, serif" }}>Détails de votre prêt</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {user?.loan_amount && (
              <div className="p-4 bg-white border border-slate-200">
                <p className="text-slate-500 text-xs">Montant demandé</p>
                <p className="text-2xl font-bold text-[#003E7E] mt-1">{formatEUR(user.loan_amount)}</p>
              </div>
            )}
            {user?.loan_duration && (
              <div className="p-4 bg-white border border-slate-200">
                <p className="text-slate-500 text-xs">Durée</p>
                <p className="text-2xl font-bold text-[#003E7E] mt-1">{user.loan_duration} ans</p>
              </div>
            )}
            {user?.loan_rate && (
              <div className="p-4 bg-white border border-slate-200">
                <p className="text-slate-500 text-xs">T.A.E.G</p>
                <p className="text-2xl font-bold text-[#003E7E] mt-1">{user.loan_rate} %</p>
              </div>
            )}
            {user?.loan_monthly && (
              <div className="p-4 bg-white border border-slate-200 lg:col-span-1">
                <p className="text-slate-500 text-xs">Mensualité</p>
                <p className="text-[11px] text-slate-500">assurance comprise</p>
                <p className="text-2xl font-bold text-[#007EAE] mt-1">{formatEUR(user.loan_monthly)}</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

// ---- Mon RIB
const RibTab = ({ user }) => {
  // Titulaire du RIB : automatique (avec civilité M./Mme) si une civilité est renseignée,
  // sinon repli sur le champ saisi manuellement (rib_holder).
  const ribHolder = isSciAccount(user)
    ? ((user?.company_name || "").trim() || user?.rib_holder || "")
    : (hasHolderCivility(user) ? formatHolders(user) : (user?.rib_holder || ""));
  const hasTextRib = user?.rib_iban || user?.rib_bic || user?.rib_bank_name || user?.rib_holder;
  const [ribPdf, setRibPdf] = useState(null);
  const [ribLoading, setRibLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchRib = async () => {
      try {
        const r = await api.get("/me/rib");
        if (!cancelled) setRibPdf(r.data || null);
      } catch {
        if (!cancelled) setRibPdf(null);
      } finally {
        if (!cancelled) setRibLoading(false);
      }
    };
    fetchRib();
    return () => { cancelled = true; };
  }, []);

  const getRibBlob = async () => {
    const currentSessionEmail = sessionStorage.getItem("hb_current_session");
    const tokenKey = currentSessionEmail ? `hb_token_${currentSessionEmail}` : "hb_token";
    const token = localStorage.getItem(tokenKey);
    const res = await fetch(`${API_BASE}/rib/${user.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error();
    return res.blob();
  };

  const viewRib = async () => {
    try {
      const blob = await getRibBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossible d'ouvrir le RIB."); }
  };

  const downloadRib = async () => {
    try {
      const blob = await getRibBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = ribPdf?.file_name || "rib.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Erreur de téléchargement."); }
  };

  if (!hasTextRib && !ribPdf && !ribLoading) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white border border-slate-200 text-center">
        <Wallet className="w-12 h-12 text-slate-400 mx-auto" />
        <h3 className="mt-4 text-lg font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
          Aucun RIB disponible pour le moment
        </h3>
        <p className="mt-2 text-slate-600 text-sm">
          Votre RIB sera disponible très prochainement dans votre espace. Vous recevrez une notification dès qu'il sera configuré.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rib-tab-content">
      <div className="flex items-center gap-2 text-slate-800">
        <Wallet className="w-5 h-5 text-[#007EAE]" />
        <h3 className="text-lg font-semibold" style={{ fontFamily: "Georgia, serif" }}>Configuration du RIB</h3>
      </div>

      {ribPdf && (
        <div className="p-4 bg-white border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid="rib-pdf-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#007EAE]/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#007EAE]" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate">Mon RIB (PDF)</p>
              <p className="text-[12px] text-slate-500 truncate">{ribPdf.file_name} • {(ribPdf.size / 1024).toFixed(1)} Ko</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={viewRib} data-testid="view-rib-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#007EAE] text-[#007EAE] hover:bg-[#007EAE]/5 text-sm font-semibold btn-press">
              <Eye className="w-4 h-4" /> Visionner
            </button>
            <button onClick={downloadRib} data-testid="download-rib-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#007EAE] hover:bg-[#006c97] text-white text-sm font-semibold btn-press">
              <Download className="w-4 h-4" /> Télécharger
            </button>
          </div>
        </div>
      )}

      {hasTextRib && (
        <div className="p-5 bg-white border border-slate-200 space-y-3">
          {ribHolder && (
            <div>
              <p className="text-[12px] font-medium text-slate-500">Titulaire du compte</p>
              <p className="text-[15px] text-slate-800">{ribHolder}</p>
            </div>
          )}
          {user?.rib_iban && (
            <div>
              <p className="text-[12px] font-medium text-slate-500">IBAN</p>
              <p className="text-[15px] text-slate-800 font-mono">{user.rib_iban}</p>
            </div>
          )}
          {user?.rib_bic && (
            <div>
              <p className="text-[12px] font-medium text-slate-500">BIC</p>
              <p className="text-[15px] text-slate-800 font-mono">{user.rib_bic}</p>
            </div>
          )}
          {user?.rib_bank_name && (
            <div>
              <p className="text-[12px] font-medium text-slate-500">Nom de la banque</p>
              <p className="text-[15px] text-slate-800">{user.rib_bank_name}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Mes Offres
const OffersTab = ({ user }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOffers = useCallback(async () => {
    try {
      const r = await api.get("/me/offers");
      setOffers((prev) => {
        const next = r.data || [];
        // Avoid re-render if nothing changed (compare ids + visibility + count)
        if (
          prev.length === next.length &&
          prev.every((p, i) => p.id === next[i]?.id && p.visible === next[i]?.visible && p.title === next[i]?.title)
        ) {
          return prev;
        }
        return next;
      });
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      await fetchOffers();
    };
    run();
    const interval = setInterval(run, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [fetchOffers]);

  const download = async (offer) => {
    try {
      // Récupérer le token de la session active de cet onglet
      const currentSessionEmail = sessionStorage.getItem("hb_current_session");
      const tokenKey = currentSessionEmail ? `hb_token_${currentSessionEmail}` : "hb_token";
      const token = localStorage.getItem(tokenKey);
      
      const url = `${API_BASE}/offers/${offer.id}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = offer.file_name || "offre.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error("Impossible de télécharger l'offre.");
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-500">Chargement de vos offres…</div>;
  }

  if (!offers.length) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white border border-slate-200 text-center">
        <FileLock2 className="w-12 h-12 text-[#007EAE] mx-auto" />
        <h3 className="mt-4 text-lg font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
          Votre offre est en cours de rédaction
        </h3>
        <p className="mt-2 text-slate-600 text-sm">
          Nos équipes préparent actuellement votre offre de financement personnalisée. Vous recevrez une notification dès qu'elle sera disponible dans votre espace.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-[#007EAE]">
          <ShieldCheck className="w-4 h-4" /> Conformité bancaire & confidentialité garanties
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
        Mes offres ({offers.length})
      </h2>
      <div className="space-y-2">
        {offers.map((o) => (
          <div key={o.id} className="flex items-center justify-between p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-[#007EAE]/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#007EAE]" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{o.title}</p>
                <p className="text-[12px] text-slate-500 truncate">{o.file_name} • {(o.size / 1024).toFixed(1)} Ko</p>
              </div>
            </div>
            <button
              onClick={() => download(o)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#007EAE] hover:bg-[#006c97] text-white text-sm font-semibold btn-press"
            >
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Télécharger</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Mon Conseiller
const ContactTab = ({ user }) => {
  const [formType, setFormType] = useState(null); // null | 'message' | 'callback'
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(user?.phone || "");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const hasAdvisor = user?.advisor_name || user?.advisor_phone || user?.advisor_email || user?.advisor_address;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Veuillez saisir un message.");
      return;
    }
    setSending(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setSending(false);
    setSubmitted(true);
    setMessage("");
    setTimeout(() => {
      setSubmitted(false);
      setFormType(null);
    }, 3000);
  };

  const handleRequestCallback = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      toast.error("Veuillez saisir un numéro de téléphone.");
      return;
    }
    setSending(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setSending(false);
    setSubmitted(true);
    setPhoneNumber(user?.phone || "");
    setTimeout(() => {
      setSubmitted(false);
      setFormType(null);
    }, 3000);
  };

  if (!hasAdvisor) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white border border-slate-200 text-center">
        <UserCheck className="w-12 h-12 text-slate-400 mx-auto" />
        <h3 className="mt-4 text-lg font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
          Aucun conseiller attribué pour le moment
        </h3>
        <p className="mt-2 text-slate-600 text-sm">
          Votre conseiller personnel sera attribué très prochainement. Vous recevrez une notification dès qu'il sera disponible.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Informations du conseiller */}
      <div className="p-6 bg-white border border-slate-200 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
          <div className="w-16 h-16 bg-[#007EAE]/10 flex items-center justify-center rounded-full overflow-hidden shrink-0">
            {user.advisor_photo ? (
              <img src={user.advisor_photo} alt={user.advisor_name || "Conseiller"} className="w-full h-full object-cover" />
            ) : (
              <UserCheck className="w-7 h-7 text-[#007EAE]" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
              {user.advisor_name || "Conseiller CaixaBank"}
            </h3>
            <p className="text-sm text-slate-500">Votre conseiller personnel</p>
          </div>
        </div>

        {user?.advisor_phone && (
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-[#007EAE] mt-0.5" />
            <div>
              <p className="text-[12px] font-medium text-slate-500">Téléphone</p>
              <a href={`tel:${user.advisor_phone}`} className="text-[15px] text-[#007EAE] hover:underline">
                {user.advisor_phone}
              </a>
            </div>
          </div>
        )}

        {user?.advisor_email && (
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#007EAE] mt-0.5" />
            <div>
              <p className="text-[12px] font-medium text-slate-500">Email</p>
              <a href={`mailto:${user.advisor_email}`} className="text-[15px] text-[#007EAE] hover:underline break-all">
                {user.advisor_email}
              </a>
            </div>
          </div>
        )}

        {user?.advisor_address && (
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#007EAE] mt-0.5" />
            <div>
              <p className="text-[12px] font-medium text-slate-500">Adresse de l'agence</p>
              <p className="text-[15px] text-slate-800">{user.advisor_address}</p>
            </div>
          </div>
        )}
      </div>

      {/* Formulaire de contact */}
      <div className="p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Georgia, serif" }}>
          Contacter votre conseiller
        </h3>

        {!formType && !submitted && (
          <div className="space-y-3">
            <button
              onClick={() => setFormType('message')}
              className="w-full flex items-center gap-3 p-4 border border-slate-300 hover:border-[#007EAE] hover:bg-[#007EAE]/5 transition-colors"
            >
              <MessageSquare className="w-5 h-5 text-[#007EAE]" />
              <div className="text-left">
                <p className="font-semibold text-slate-800">Envoyer un message</p>
                <p className="text-xs text-slate-500">Posez une question à votre conseiller</p>
              </div>
            </button>
            <button
              onClick={() => setFormType('callback')}
              className="w-full flex items-center gap-3 p-4 border border-slate-300 hover:border-[#007EAE] hover:bg-[#007EAE]/5 transition-colors"
            >
              <PhoneCall className="w-5 h-5 text-[#007EAE]" />
              <div className="text-left">
                <p className="font-semibold text-slate-800">Demander un rappel</p>
                <p className="text-xs text-slate-500">Votre conseiller vous rappellera</p>
              </div>
            </button>
          </div>
        )}

        {formType === 'message' && !submitted && (
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div>
              <label className="text-[12.5px] font-medium text-slate-700">Votre message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="Décrivez votre demande..."
                className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormType(null)}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold"
              >
                Annuler
              </button>
              <button
                disabled={sending}
                type="submit"
                className="flex-1 px-5 py-2 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold disabled:opacity-70"
              >
                {sending ? "Envoi en cours..." : "Envoyer le message"}
              </button>
            </div>
          </form>
        )}

        {formType === 'callback' && !submitted && (
          <form onSubmit={handleRequestCallback} className="space-y-4">
            <div>
              <label className="text-[12.5px] font-medium text-slate-700">Votre numéro de téléphone</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+33 6 12 34 56 78"
                className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
              />
            </div>
            <div>
              <label className="text-[12.5px] font-medium text-slate-700">Motif du rappel (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Décrivez brièvement l'objet du rappel..."
                className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormType(null)}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold"
              >
                Annuler
              </button>
              <button
                disabled={sending}
                type="submit"
                className="flex-1 px-5 py-2 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold disabled:opacity-70"
              >
                {sending ? "Envoi en cours..." : "Demander un rappel"}
              </button>
            </div>
          </form>
        )}

        {submitted && (
          <div className="p-6 bg-emerald-50 border border-emerald-200 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h4 className="mt-3 text-lg font-semibold text-emerald-800">
              {formType === 'message' ? 'Message envoyé !' : 'Demande de rappel envoyée !'}
            </h4>
            <p className="mt-2 text-sm text-emerald-700">
              {formType === 'message'
                ? 'Votre conseiller vous répondra dans les plus brefs délais.'
                : 'Votre conseiller vous rappellera très prochainement.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Informations
const InfoTab = ({ user, onRefresh }) => {
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
  });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      await onRefresh();
      toast.success("Informations mises à jour.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe mis à jour.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du changement de mot de passe.");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="p-6 bg-white border border-slate-200 space-y-4">
        <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>
          Informations personnelles
        </h3>
        {displayName(user) && (
          <div className="p-3 bg-slate-50 border border-slate-200">
            <p className="text-[12px] font-medium text-slate-500">{isSciAccount(user) ? "Société (SCI)" : "Titulaire(s) du compte"}</p>
            <p className="text-[15px] font-semibold text-slate-800">{displayName(user)}</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Prénom</label>
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Nom</label>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
        </div>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">E-mail</label>
          <input value={user?.email || ""} disabled
            className="w-full mt-1 px-3 py-2.5 border border-slate-200 bg-slate-50 text-slate-500 text-[15px] rounded-none" />
        </div>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">Téléphone</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+33 6 12 34 56 78"
            className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
        </div>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">Adresse</label>
          <div className="mt-1">
            <AddressAutocomplete value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Commencez à taper votre adresse…" />
          </div>
        </div>
        <button disabled={saving} type="submit"
          className="w-full sm:w-auto px-6 py-2.5 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold btn-press disabled:opacity-70">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </form>

      <form onSubmit={savePwd} className="p-6 bg-white border border-slate-200 space-y-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Georgia, serif" }}>
          <Lock className="w-5 h-5 text-[#007EAE]" /> Changer mon mot de passe
        </h3>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">Mot de passe actuel</label>
          <input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
        </div>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">Nouveau mot de passe</label>
          <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
        </div>
        <div>
          <label className="text-[12.5px] font-medium text-slate-700">Confirmer le nouveau mot de passe</label>
          <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
        </div>
        <button disabled={pwSaving} type="submit"
          className="w-full sm:w-auto px-6 py-2.5 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold btn-press disabled:opacity-70">
          {pwSaving ? "Mise à jour…" : "Mettre à jour"}
        </button>
      </form>
    </div>
  );
};

const ClientDashboard = () => {
  const { user, refresh, logout } = useAuth();
  const [tab, setTab] = useState("account");
  const previousStatusRef = useRef(user?.status);
  const previousAmountsRef = useRef({
    credit_mobilier: user?.credit_mobilier,
    apport: user?.apport,
  });

  // Initial refresh on mount
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  // Live auto-refresh: poll user data every 5s (silent, no spinners)
  useEffect(() => {
    const POLL_MS = 5000;
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      refresh();
    };
    const interval = setInterval(tick, POLL_MS);
    // Also refresh immediately when tab regains focus (instant feedback)
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  // Detect live status changes (blocked / approved) made by admin
  useEffect(() => {
    if (!user) return;
    const prev = previousStatusRef.current;
    if (prev && prev !== user.status) {
      if (user.status === "blocked") {
        toast.error("Votre compte a été bloqué par votre banque.", { duration: 4000 });
        // Logout after a short delay so the user sees the message
        setTimeout(() => logout(), 2200);
      } else if (user.status === "approved" && prev !== "approved") {
        toast.success("Votre dossier vient d'être approuvé par votre banque !", { duration: 5000 });
      } else if (user.status === "pending" && prev === "approved") {
        toast.info("Votre dossier est repassé en attente d'approbation.", { duration: 4000 });
      }
    }
    previousStatusRef.current = user.status;
  }, [user?.status, logout, user]);

  // Detect live amount changes (credit_mobilier / apport) by admin
  useEffect(() => {
    if (!user) return;
    const prev = previousAmountsRef.current;
    const credChanged = prev.credit_mobilier !== undefined && prev.credit_mobilier !== user.credit_mobilier;
    const apportChanged = prev.apport !== undefined && prev.apport !== user.apport;
    if (credChanged || apportChanged) {
      toast.info("Votre conseiller vient de mettre à jour votre dossier.", { duration: 3500 });
    }
    previousAmountsRef.current = {
      credit_mobilier: user.credit_mobilier,
      apport: user.apport,
    };
  }, [user?.credit_mobilier, user?.apport, user]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Tabs */}
        <div className="bg-slate-100 border border-slate-200 flex overflow-x-auto">
          <TabBtn active={tab === "account"} onClick={() => setTab("account")} icon={Wallet}>Mon Compte</TabBtn>
          <TabBtn active={tab === "offers"} onClick={() => setTab("offers")} icon={FileText}>Mes Offres</TabBtn>
          <TabBtn active={tab === "rib"} onClick={() => setTab("rib")} icon={Wallet}>Mon RIB</TabBtn>
          <TabBtn active={tab === "contact"} onClick={() => setTab("contact")} icon={UserCheck}>Mon Conseiller</TabBtn>
          <TabBtn active={tab === "info"} onClick={() => setTab("info")} icon={User}>Mes Informations</TabBtn>
        </div>
        <div className="mt-6">
          {tab === "account" && <AccountTab user={user} />}
          {tab === "offers" && <OffersTab user={user} />}
          {tab === "rib" && <RibTab user={user} />}
          {tab === "contact" && <ContactTab user={user} />}
          {tab === "info" && <InfoTab user={user} onRefresh={refresh} />}
        </div>
      </main>
      <FooterCaixaBank />
    </div>
  );
};

export default ClientDashboard;
